import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { useAnalyticsContext } from '../contexts/AnalyticsContext';
import { setConsent } from '../utils/analytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Cookie,
  Shield,
  BarChart3,
  Target,
  Settings,
  ExternalLink,
  CheckCircle,
  X,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

interface CookiePreferences {
  essential: boolean; // Always true
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

interface CookieConsentPopupProps {
  onPreferencesChange: (preferences: CookiePreferences) => void;
  onOpenAccountSettings: () => void;
}

const STORAGE_KEY = 'olispices-cookie-preferences';

export function CookieConsentPopup({
  onPreferencesChange,
  onOpenAccountSettings,
}: CookieConsentPopupProps) {
  const analytics = useAnalyticsContext();
  const [isVisible, setIsVisible] = useState(false);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    timestamp: 0,
  });

  // Check if user has already made cookie choices
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CookiePreferences;
        // Check if preferences are older than 6 months (re-consent required)
        const sixMonthsAgo = Date.now() - 6 * 30 * 24 * 60 * 60 * 1000;
        if (parsed.timestamp > sixMonthsAgo) {
          setPreferences(parsed);
          // Only call onPreferencesChange without showing toast for existing preferences
          onPreferencesChange(parsed);
          return;
        }
      } catch (e) {
        console.error('Error parsing stored cookie preferences:', e);
      }
    }

    // Show popup if no valid stored preferences
    setIsVisible(true);
  }, []); // Remove onPreferencesChange from dependencies to prevent loops

  const savePreferences = (newPreferences: CookiePreferences) => {
    const prefsWithTimestamp = {
      ...newPreferences,
      timestamp: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefsWithTimestamp));
    setPreferences(prefsWithTimestamp);

    // Update GA4 consent
    setConsent({
      analytics_storage: prefsWithTimestamp.analytics ? 'granted' : 'denied',
      ad_storage: prefsWithTimestamp.marketing ? 'granted' : 'denied',
      functionality_storage: 'granted',
      personalization_storage: prefsWithTimestamp.analytics
        ? 'granted'
        : 'denied',
      security_storage: 'granted',
    });

    // Track consent event
    analytics.trackEvent('cookie_consent', {
      analytics: prefsWithTimestamp.analytics,
      marketing: prefsWithTimestamp.marketing,
    });

    // Call the parent callback
    onPreferencesChange(prefsWithTimestamp);

    // Hide the popup
    setIsVisible(false);
    setIsCustomizing(false);

    // Show success message only when actually saving preferences
    toast.success('Cookie preferences saved successfully', {
      description:
        'Your choices have been saved and will be remembered for 6 months.',
    });
  };

  const handleAcceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    });
  };

  const handleRejectAll = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    });
  };

  const handleSaveCustom = () => {
    savePreferences(preferences);
  };

  const updatePreference = (key: keyof CookiePreferences, value: boolean) => {
    if (key === 'essential') return; // Essential cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm"
        style={{
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
      />

      {/* Popup */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 lg:p-6">
        <div className="max-w-4xl mx-auto">
          <Card
            className="border-0 shadow-neural-lg"
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(25px)',
              WebkitBackdropFilter: 'blur(25px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            {/* Neural gradient overlay */}
            <div
              className="absolute inset-0 opacity-30 pointer-events-none rounded-xl"
              style={{
                background:
                  'linear-gradient(135deg, rgba(245, 158, 11, 0.03) 0%, rgba(217, 119, 6, 0.01) 50%, rgba(180, 83, 9, 0.03) 100%)',
              }}
            />

            <div className="relative">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="p-2 rounded-2xl"
                      style={{
                        background:
                          'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.15) 100%)',
                      }}
                    >
                      <Cookie className="h-6 w-6 text-amber-700" />
                    </div>
                    <div>
                      <CardTitle className="text-lg sm:text-xl text-gray-900">
                        Cookie Preferences
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">
                        <span className="sm:hidden">We use cookies to improve your experience</span>
                        <span className="hidden sm:inline">We use cookies to enhance your experience on Olispices</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span>GDPR Compliant</span>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 sm:space-y-6">
                {!isCustomizing ? (
                  // Simple View
                  <>
                    <div className="space-y-3">
                      <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                        We use essential cookies for basic functionality and optional cookies to improve your experience.
                      </p>

                      {/* Mobile: Compact info, Desktop: Full info */}
                      <div className="block sm:hidden">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={onOpenAccountSettings}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-0 h-auto text-sm"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Privacy Settings
                        </Button>
                      </div>

                      <div
                        className="hidden sm:block p-4 rounded-xl"
                        style={{
                          background: 'rgba(59, 130, 246, 0.05)',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="text-gray-700 mb-2">
                              <strong>Your data rights:</strong> You can change
                              your preferences anytime, download your data, or
                              delete your account in your privacy settings.
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={onOpenAccountSettings}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-0 h-auto"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Manage Privacy Settings
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
                      <Button
                        onClick={handleAcceptAll}
                        className="flex-1 h-10 sm:h-12 bg-amber-900 hover:bg-amber-800 text-white rounded-xl transition-all duration-300 text-sm sm:text-base"
                      >
                        Accept All
                      </Button>

                      <Button
                        variant="outline"
                        onClick={handleRejectAll}
                        className="flex-1 h-10 sm:h-12 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl text-sm sm:text-base"
                      >
                        Reject Optional
                      </Button>

                      <Button
                        variant="ghost"
                        onClick={() => setIsCustomizing(true)}
                        className="flex-1 h-10 sm:h-12 text-amber-700 hover:bg-amber-50 rounded-xl text-sm sm:text-base"
                      >
                        <Settings className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline">Customize</span>
                        <span className="sm:hidden">Options</span>
                      </Button>
                    </div>
                  </>
                ) : (
                  // Detailed Customization View
                  <>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">
                          Cookie Categories
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsCustomizing(false)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Essential Cookies */}
                      <div
                        className="p-4 rounded-xl opacity-60"
                        style={{
                          background: 'rgba(34, 197, 94, 0.05)',
                          border: '1px solid rgba(34, 197, 94, 0.2)',
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Shield className="h-5 w-5 text-green-600" />
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                Essential Cookies
                              </h4>
                              <p className="text-sm text-gray-600">
                                Required for basic website functionality
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="organic-badge text-xs">
                              Always Active
                            </Badge>
                            <Switch checked={true} disabled />
                          </div>
                        </div>
                        <p className="text-xs text-gray-600">
                          <span className="sm:hidden">Required for basic website functionality.</span>
                          <span className="hidden sm:inline">These cookies are necessary for the website to
                          function and cannot be disabled. They include security
                          features, shopping cart functionality, and your
                          account access.</span>
                        </p>
                      </div>

                      {/* Analytics Cookies */}
                      <div
                        className="p-4 rounded-xl"
                        style={{
                          background: preferences.analytics
                            ? 'rgba(59, 130, 246, 0.05)'
                            : 'rgba(107, 114, 128, 0.05)',
                          border: `1px solid ${
                            preferences.analytics
                              ? 'rgba(59, 130, 246, 0.2)'
                              : 'rgba(107, 114, 128, 0.2)'
                          }`,
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <BarChart3 className="h-5 w-5 text-blue-600" />
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                Analytics Cookies
                              </h4>
                              <p className="text-sm text-gray-600">
                                Help us understand how you use our site
                              </p>
                            </div>
                          </div>
                          <Switch
                            checked={preferences.analytics}
                            onCheckedChange={(checked: boolean) =>
                              updatePreference('analytics', checked)
                            }
                          />
                        </div>
                        <p className="text-xs text-gray-600">
                          <span className="sm:hidden">Help us understand how you use our site.</span>
                          <span className="hidden sm:inline">These cookies collect anonymous information about how
                          visitors use our website, helping us improve
                          performance and user experience. Data includes page
                          views, time spent on site, and navigation patterns.</span>
                        </p>
                      </div>

                      {/* Marketing Cookies */}
                      <div
                        className="p-4 rounded-xl"
                        style={{
                          background: preferences.marketing
                            ? 'rgba(168, 85, 247, 0.05)'
                            : 'rgba(107, 114, 128, 0.05)',
                          border: `1px solid ${
                            preferences.marketing
                              ? 'rgba(168, 85, 247, 0.2)'
                              : 'rgba(107, 114, 128, 0.2)'
                          }`,
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Target className="h-5 w-5 text-purple-600" />
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                Marketing Cookies
                              </h4>
                              <p className="text-sm text-gray-600">
                                Enable personalized ads and content
                              </p>
                            </div>
                          </div>
                          <Switch
                            checked={preferences.marketing}
                            onCheckedChange={(checked: boolean) =>
                              updatePreference('marketing', checked)
                            }
                          />
                        </div>
                        <p className="text-xs text-gray-600">
                          <span className="sm:hidden">Enable personalized ads and content.</span>
                          <span className="hidden sm:inline">These cookies track your activity across websites to
                          deliver targeted advertising. They help us show you
                          relevant spice recommendations and special offers
                          based on your interests.</span>
                        </p>
                      </div>
                    </div>

                    {/* Mobile: Hide additional info, Desktop: Show full info */}
                    <div className="hidden md:block">
                      <Separator />

                      {/* Cookie Details & Links */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-gray-900 text-sm">
                          Additional Information
                        </h4>
                        <div className="grid md:grid-cols-2 gap-3 text-xs">
                          <div
                            className="p-3 rounded-lg"
                            style={{
                              background: 'rgba(245, 158, 11, 0.05)',
                              border: '1px solid rgba(245, 158, 11, 0.2)',
                            }}
                          >
                            <h5 className="font-semibold text-gray-900 mb-1">
                              Cookie Duration
                            </h5>
                            <p className="text-gray-600">
                              Most cookies expire after 1-2 years. Essential
                              cookies remain until you clear your browser data.
                            </p>
                          </div>
                          <div
                            className="p-3 rounded-lg"
                            style={{
                              background: 'rgba(34, 197, 94, 0.05)',
                              border: '1px solid rgba(34, 197, 94, 0.2)',
                            }}
                          >
                            <h5 className="font-semibold text-gray-900 mb-1">
                              Your Control
                            </h5>
                            <p className="text-gray-600">
                              Change preferences anytime in your account settings
                              or browser settings.
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 pt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={onOpenAccountSettings}
                            className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 text-xs"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Privacy Policy
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={onOpenAccountSettings}
                            className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 text-xs"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Data Rights
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Mobile: Compact privacy links */}
                    <div className="block md:hidden">
                      <div className="flex flex-wrap gap-2 justify-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={onOpenAccountSettings}
                          className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 text-xs"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Privacy Policy
                        </Button>
                      </div>
                    </div>

                    {/* Custom Save Button */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
                      <Button
                        onClick={handleSaveCustom}
                        className="flex-1 h-10 sm:h-12 bg-amber-900 hover:bg-amber-800 text-white rounded-xl text-sm sm:text-base"
                      >
                        <span className="sm:hidden">Save</span>
                        <span className="hidden sm:inline">Save My Preferences</span>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setIsCustomizing(false)}
                        className="sm:w-auto h-10 sm:h-12 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl text-sm sm:text-base"
                      >
                        <span className="sm:hidden">Back</span>
                        <span className="hidden sm:inline">Back to Simple View</span>
                      </Button>
                    </div>
                  </>
                )}

                {/* Footer */}
                <div
                  className="text-xs text-gray-500 pt-3 border-t"
                  style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}
                >
                  <p>
                    <span className="sm:hidden">
                      Your preferences will be saved for 6 months.
                    </span>
                    <span className="hidden sm:inline">
                      By using our website, you agree to our use of cookies as
                      described above. Your preferences will be saved for 6
                      months, after which we'll ask for your consent again.
                    </span>
                  </p>
                </div>
              </CardContent>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
